import { useFormikContext } from "formik";
import React, { useEffect, useState } from "react";
// Images
import uploadImage from "../../assets/images/upload.svg";
import { useTranslation } from "react-i18next";
import { NumberInputKeyDown } from "../../helpers/numberInput";
import ImageFilePreview from "../../components/ImageFilePreview";
import { toast } from "react-toastify";
import PreviewModal from "../../components/PreviewModal";
import document_img from "../../assets/images/document-img.jpg";

const Step4 = () => {
  const { t } = useTranslation();
  const { handleChange, handleBlur, values, touched, errors, setFieldValue } =
    useFormikContext();

  const [ibanAttachmentType, setIbanAttachmentType] = useState(null);
  const [ibanAttachments, setIbanAttachments] = useState(null);
  const [previewModal, setPreviewModal] = useState({
    show: false,
    title: "",
    fileUrl: "",
    fileType: "",
  });

  // Re-initialize preview state from Formik File object when component mounts
  useEffect(() => {
    const file = values?.ibanNumberAttachment;
    if (file instanceof File && !ibanAttachments) {
      const fileType = file.type;
      setIbanAttachmentType(fileType);
      if (fileType?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setIbanAttachments(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setIbanAttachments(fileURL);
      }
    }
  }, [values?.ibanNumberAttachment]);

  const handleRemoveIbanDocument = () => {
    setIbanAttachments(null);
    setIbanAttachmentType(null);
    setFieldValue("ibanNumberAttachment", null);
    const fileInput = document.getElementById("ibanNumberAttachment");
    if (fileInput) fileInput.value = "";
  };

  const getFileTypeFromUrl = (url) => {
    if (!url || typeof url !== "string") return "";
    const ext = url.split('.').pop().toLowerCase();
    if (["png", "jpg", "jpeg"].includes(ext)) return "image";
    if (ext === "pdf") return "pdf";
    if (ext === "doc" || ext === "docx") return "doc";
    return "";
  };

  const handleViewDocument = (title, attachment, attachmentType, formikValue) => {
    let fileUrl = "";
    let fileType = "";
    if (attachment && typeof attachment === "string") {
      fileUrl = attachment;
      fileType = attachmentType?.startsWith("image/")
        ? "image"
        : attachmentType?.includes("pdf")
          ? "pdf"
          : attachmentType?.includes("word") || attachmentType?.includes("doc")
            ? "doc"
            : "";
    } else if (formikValue) {
      if (formikValue instanceof File) {
        fileUrl = URL.createObjectURL(formikValue);
        fileType = formikValue.type?.startsWith("image/")
          ? "image"
          : formikValue.type?.includes("pdf")
            ? "pdf"
            : formikValue.name?.toLowerCase().endsWith(".doc") || formikValue.name?.toLowerCase().endsWith(".docx")
              ? "doc"
              : "";
      } else if (typeof formikValue === "string") {
        fileUrl = formikValue;
        fileType = getFileTypeFromUrl(fileUrl);
      }
    }

    if (fileType === "doc") {
      const officeUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(fileUrl)}`;
      window.open(officeUrl, "_blank", "noopener,noreferrer");
    } else {
      setPreviewModal({
        show: true,
        title,
        fileUrl,
        fileType,
      });
    }
  };

  const handleBankCertificateChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const allowedExtensions = [".jpg", ".jpeg", ".png", ".pdf", ".doc"];

    const fileTypeValid = allowedTypes.includes(file.type);
    const fileExtensionValid = allowedExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext)
    );

    if (!fileTypeValid || !fileExtensionValid) {
      toast.error(
        t("Invalid file type. Supported formats: .png, .jpg, .pdf, .doc.")
      );
      e.target.value = "";
      return;
    }
    if (file) {
      const fileType = file.type;
      setIbanAttachmentType(fileType);
      if (fileType?.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setIbanAttachments(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        const fileURL = URL.createObjectURL(file);
        setIbanAttachments(fileURL);
      }
      setFieldValue("ibanNumberAttachment", file);
    }
  };

  return (
    <>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="beneficiaryName"
          placeholder={`${t("Beneficiary Name")}`}
          required
          onChange={handleChange}
          value={values.beneficiaryName}
          onBlur={handleBlur}
        />
        {touched.beneficiaryName && errors.beneficiaryName && (
          <div className="text-danger">{errors.beneficiaryName}</div>
        )}
      </div>
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="bankName"
          placeholder={`${t("Bank Name")}`}
          required
          onChange={handleChange}
          value={values.bankName}
          onBlur={handleBlur}
        />
        {touched.bankName && errors.bankName && (
          <div className="text-danger">{errors.bankName}</div>
        )}
      </div>
      {/* <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="accountNumber"
          placeholder={`${t("Account Number")} *`}
          required
          onChange={handleChange}
          value={values.accountNumber}
          onBlur={handleBlur}
          onKeyDown={(e) => NumberInputKeyDown(e)}
        />
        {touched.accountNumber && errors.accountNumber && (
          <div className="text-danger">{errors.accountNumber}</div>
        )}
      </div> */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="ibanNumber"
          placeholder={`${t("IBAN Number")}`}
          required
          onChange={handleChange}
          value={values.ibanNumber}
          onBlur={handleBlur}
        />
        {touched.ibanNumber && errors.ibanNumber && (
          <div className="text-danger">{errors.ibanNumber}</div>
        )}
      </div>
      <div className="upload-container d-flex align-items-center mb-3">
        <div
          className="upload-box"
          onClick={() =>
            document.getElementById("ibanNumberAttachment").click()
          }
        >
          {values?.ibanNumberAttachment && !ibanAttachments ? (
            <img
              src={values?.ibanNumberAttachment}
              alt={t("Upload Icon")}
              className="upload-icon"
            />
          ) : ibanAttachmentType && !ibanAttachmentType.startsWith("image/") ? (
            <a href={ibanAttachments} target="_blank" rel="noopener noreferrer">
              <img
                src={document_img}
                alt="Document Icon"
                className="upload-icon"
                style={{ cursor: "pointer" }}
              />
            </a>
          ) : (
            ibanAttachments ?
              <img
                src={ibanAttachments}
                alt="IBAN document uploaded successfully"
              />
              :
              <img src={uploadImage} alt={t("Upload Icon")} className="upload-icon" />
          )}
          <input
            type="file"
            id="ibanNumberAttachment"
            style={{ display: "none" }}
            onChange={(e) => handleBankCertificateChange(e)}
            accept=".png,.jpg,.jpeg"
          />
        </div>
        <div className="upload-description ms-3">
          <p>{`${t("IBAN Number Document")}`}</p>
          {values?.ibanNumberAttachment ? (
            <>
              <p className="mb-0">{t("File Uploaded Successfully")}</p>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-link text-decoration-none"
                  style={{ padding: 0 }}
                  onClick={() =>
                    handleViewDocument(
                      t("IBAN Number Document"),
                      ibanAttachments,
                      ibanAttachmentType,
                      values?.ibanNumberAttachment
                    )
                  }
                >
                  {t("View Document")}
                </button>
                <span className="text-muted">|</span>
                <button
                  type="button"
                  className="btn text-danger text-underline-none"
                  style={{ padding: 0 }}
                  onClick={handleRemoveIbanDocument}
                >
                  {t("Remove Document")}
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="mb-0">{t("Maximum file size: Up to 2 MB")}</p>
              <p className="mb-0">{t("Supported format: .png, .jpg")}</p>
            </>
          )}
          <p id="fileName1" className="mt-2 text-muted"></p>
        </div>
      </div>
      {touched.ibanNumberAttachment && errors.ibanNumberAttachment && (
        <div className="text-danger">{errors.ibanNumberAttachment}</div>
      )}
      <PreviewModal
        show={previewModal.show}
        onHide={() => setPreviewModal({ ...previewModal, show: false })}
        title={previewModal.title}
        fileUrl={previewModal.fileUrl}
        fileType={previewModal.fileType}
      />
    </>
  );
};

export default Step4;

{
  /* */
}

{
  /* <div className="mb-3">
        <input
          type="text"
          className="form-control"
          name="ibanNumber"
          placeholder="IBAN Number"
          required
          onChange={handleChange}
          value={values.ibanNumber}
          onBlur={handleBlur}
        />
        {touched.ibanNumber && errors.ibanNumber && (
          <div className="text-danger">{errors.ibanNumber}</div>
        )}
      </div> */
}
